"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.clearMomentCache = exports.cachedMoment = void 0;
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const is_defined_1 = require("./is-defined");
// Port of yavorsky/moment-cache (https://github.com/yavorsky/moment-cache) adapted for this
// codebase. It caches parsed Moment instances keyed by their input, so repeated
// `moment(sameInput)` calls skip moment's construction/parse pipeline
// (createLocal -> createLocalOrUTC -> createFromConfig), which profiling showed to be a hot
// cost in the calendar render path. A cache hit returns a `.clone()` of the stored moment,
// which is far cheaper than re-parsing a string in the workspace timezone.
//
// Two adaptations vs. the original library, both required for correctness here:
//   1. The cache is cleared whenever the default timezone changes (see setTimezone in
//      ./moment.js). Cached moments carry the zone they were parsed in, so a workspace /
//      timezone switch would otherwise return stale moments in the previous zone.
//   2. The result is always a clone by default, so callers can safely mutate it
//      (.add()/.startOf()/.endOf()) without corrupting the shared cached instance - the
//      calendar mutates moments heavily. Pass clone=false only for read-only use where the
//      result will never be mutated.
let cache = {};
// build a stable cache key from the input, or null for inputs we must not cache:
// existing Moment objects, arrays/objects, and - importantly - `moment()` with no input
// (i.e. "now"), which must return a fresh value on every call
const keyOf = (date, format) => {
    if (typeof date === 'string')
        return (0, is_defined_1.isValue)(format) ? `${format} ${date}` : date;
    if (typeof date === 'number')
        return `#${date}`;
    if (date instanceof Date)
        return `@${date.getTime()}`;
    return null;
};
const cachedMoment = (date, format, clone = true) => {
    const key = keyOf(date, format);
    if (!(0, is_defined_1.isValue)(key))
        return (0, moment_timezone_1.default)(date, format);
    let cached = cache[key];
    if (!(0, is_defined_1.isValue)(cached)) {
        cached = cache[key] = (0, moment_timezone_1.default)(date, format);
    }
    return clone ? cached.clone() : cached;
};
exports.cachedMoment = cachedMoment;
// clears every cached moment - called on a timezone change so stale, wrong-zone
// instances are never handed out
const clearMomentCache = () => {
    cache = {};
};
exports.clearMomentCache = clearMomentCache;
